import React, { FC, Suspense } from 'react';
import { Skeleton } from '@city-sage/aurum-avis-labs-components';
import { disableMapboxTelemetry } from '../../lib/map/disable-mapbox-telemetry';

let mapboxReady: Promise<typeof import('react-map-gl/mapbox')> | null = null;

/** Mapbox GL and its CSS load only when a map component renders. */
function loadMapboxRuntime() {
  if (!mapboxReady) {
    mapboxReady = (async () => {
      const mapboxModule = await import('mapbox-gl');
      const mapbox = (mapboxModule.default ?? mapboxModule) as {
        config?: object;
      };
      disableMapboxTelemetry(mapbox);
      const namedConfig = (mapboxModule as { config?: object }).config;
      if (namedConfig && namedConfig !== mapbox.config) {
        disableMapboxTelemetry({ config: namedConfig });
      }
      await import('mapbox-gl/dist/mapbox-gl.css');
      return import('react-map-gl/mapbox');
    })();
  }
  return mapboxReady;
}

// Lazy load Mapbox components
const LazyMap = React.lazy(() =>
  loadMapboxRuntime().then((module) => ({ default: module.Map }))
);

const LazyMarker = React.lazy(() =>
  loadMapboxRuntime().then((module) => ({ default: module.Marker }))
);

const LazySource = React.lazy(() =>
  loadMapboxRuntime().then((module) => ({ default: module.Source }))
);

const LazyLayer = React.lazy(() =>
  loadMapboxRuntime().then((module) => ({ default: module.Layer }))
);

const LazyGeolocateControl = React.lazy(() =>
  loadMapboxRuntime().then((module) => ({ default: module.GeolocateControl }))
);

const LazyFullscreenControl = React.lazy(() =>
  loadMapboxRuntime().then((module) => ({ default: module.FullscreenControl }))
);

// Map Loading Component
const MapLoading: FC<{ height?: string; className?: string }> = ({
  height = 'h-48',
  className = '',
}) => (
  <div className={`${height} ${className} relative`}>
    <Skeleton className="w-full h-full" />
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="text-sm text-muted-foreground bg-background/80 px-3 py-1 rounded">
        Loading map...
      </div>
    </div>
  </div>
);

// Re-export components with lazy loading
export const Map = React.forwardRef<any, any>((props, ref) => {
  return (
    <Suspense
      fallback={
        <MapLoading height={props.style?.height} className={props.className} />
      }
    >
      <LazyMap ref={ref} {...props} collectResourceTiming={false} />
    </Suspense>
  );
});

export const Marker = React.forwardRef<any, any>((props, ref) => (
  <Suspense fallback={null}>
    <LazyMarker ref={ref} {...props} />
  </Suspense>
));

export const Source = React.forwardRef<any, any>((props, ref) => (
  <Suspense fallback={null}>
    <LazySource ref={ref} {...props} />
  </Suspense>
));

export const Layer = React.forwardRef<any, any>((props, ref) => (
  <Suspense fallback={null}>
    <LazyLayer ref={ref} {...props} />
  </Suspense>
));

export const GeolocateControl = React.forwardRef<any, any>((props, ref) => (
  <Suspense fallback={null}>
    <LazyGeolocateControl ref={ref} {...props} />
  </Suspense>
));

export const FullscreenControl = React.forwardRef<any, any>((props, ref) => (
  <Suspense fallback={null}>
    <LazyFullscreenControl ref={ref} {...props} />
  </Suspense>
));

// Set display names for better debugging
Map.displayName = 'LazyMap';
Marker.displayName = 'LazyMarker';
Source.displayName = 'LazySource';
Layer.displayName = 'LazyLayer';
GeolocateControl.displayName = 'LazyGeolocateControl';
FullscreenControl.displayName = 'LazyFullscreenControl';

// Re-export MapRef type (this doesn't affect bundle size)
export type { MapRef } from 'react-map-gl/mapbox';
